<template>
  <div class="col-md-6 offset-md-3">
    <div class="login-form mt-4 p-4">
      <div class="row g-3">
        <h4>Подтвердите, что вы владелец восстанавливаемой почты</h4>
        <p>Вам на почту отправлено письмо с кодом, укажите пожалуйста тут этот код.</p>
        <p>Если что-то пошло не так, можно <a href="#" @click="handleClickActionRestoreResend" id="btn-resend">Отправить</a> повторно.</p>
        <div class="col-12 status-placeholder">
          <status-label :message="emailProgress" :style="emailProgressStyle"></status-label>
        </div>
        <div class="col-12">
          <label>Почта</label>
          <input type="email" name="email" class="form-control" placeholder="Email" v-model="restorePasswordEmail" readonly disabled>
        </div>
        <div class="col-12">
          <label>Код</label>
          <input type="text" name="code" class="form-control" placeholder="Code" v-model="code">
        </div>
        <div class="col-12 error-placeholder">
          <error-label/>
        </div>
        <div class="col-12">
          <button type="submit" class="btn btn-dark float-end ms-3" @click="handleClickActionRestoreConfirm" :disabled="disabledSubmit">Подтвердить</button>
          <button type="submit" class="btn btn-dark float-end" @click="handleClickActionRestoreConfirmCancel">Отмена</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import ErrorLabel from "@/components/ui/ErrorLabel.vue";
import { latestMessage, type WebSocketReceiveMessage, type WebSocketMessageEmpty, type WebSocketMessageError, type WebSocketMessageWithFields } from "@/plugins/ws";
import StatusLabel from "@/components/ui/StatusLabel.vue";
import { useToast } from "@/composables/useToast";
import {RootState} from "@/store";

defineOptions({
  name: 'ComponentsAuthRestoreCode',
});

const emit = defineEmits<{
  switchMode: [mode: string];
}>();

const store = useStore<RootState>();

// Типы для WebSocket сообщений компонента RestoreCode
type RestoreEmailProgressMessage = WebSocketMessageWithFields<'auth-restore-email-progress', {
  emailProgressMessage: string;
  emailProgressType: string;
}>;

type RestoreCancelMessage = WebSocketMessageEmpty<'auth-restore-cancel'>;
type RestoreResendMessage = WebSocketMessageEmpty<'auth-restore-resend'>;
type RestoreConfirmMessage = WebSocketMessageEmpty<'auth-restore-confirm'>;
type RestoreCodeFailMessage = WebSocketMessageError<'auth-restore-code-fail'>;

// Объединенный тип для всех сообщений
type RestoreCodeWebSocketMessageType =
    | RestoreEmailProgressMessage
    | RestoreCancelMessage
    | RestoreResendMessage
    | RestoreConfirmMessage
    | RestoreCodeFailMessage;

const internalInstance = getCurrentInstance();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const wsSend = internalInstance?.appContext.config.globalProperties.$wsSend as (signal: string, payload: any) => void;

const code = ref('');
const emailProgress = ref('Prepare to sending ...');
const emailProgressStyle = ref('bg-info');
const latestMessageValue = ref(latestMessage);
const disabledSubmit = ref(false);

const user = computed(() => store.state.auth.user);
const restorePasswordEmail = computed(() => store.state.auth.restorePasswordEmail);

const setErrorReason = (reason: string) => store.dispatch('errorLabel/setErrorReason', reason);
const unsetErrorReason = () => store.dispatch('errorLabel/unsetErrorReason');

const { showToast } = useToast();

watch(code, () => {
  unsetErrorReason();
});

// WebSocket message handler
const handleWebSocketMessage = (data: RestoreCodeWebSocketMessageType): void => {
  if (!data?.signal) return;

  switch (data.signal) {
    case 'auth-restore-email-progress':
      signalEmailProgress(data.emailProgressMessage, data.emailProgressType);
      break;
    case 'auth-restore-cancel':
      signalRestoreCancel();
      break;
    case 'auth-restore-resend':
      signalRestoreResend();
      break;
    case 'auth-restore-confirm':
      signalRestoreConfirm();
      break;
    case 'auth-restore-code-fail':
      signalRestoreCodeFail(data.reason);
      break;
  }
};

watch(latestMessageValue, handleWebSocketMessage);

const handleClickActionRestoreResend = () => {
  wsSend('authRestoreResend', {});
};

const handleClickActionRestoreConfirm = () => {
  wsSend('authRestoreCode', {
    email: restorePasswordEmail.value,
    code: code.value,
  });
  disabledSubmit.value = true;
};

const handleClickActionRestoreConfirmCancel = () => {
  wsSend('authRestoreCancel', {});
};

const signalRestoreCodeFail = (payload: string) => {
  setErrorReason(payload);
  disabledSubmit.value = false;
  showToast('Ошибка подтверждения', payload, 15000);
};

const signalRestoreCancel = () => {
  unsetErrorReason();
  disabledSubmit.value = false;
  store.dispatch('auth/wsUnsetUser');
  emit('switchMode', 'restore');
};

const signalEmailProgress = (message: string | null, type: string | null) => {
  unsetErrorReason();
  emailProgress.value = message === null ? 'Prepare to sending ...' : message;
  switch (type) {
    case 'error':
      emailProgressStyle.value = 'bg-danger';
      break;
    case null:
    case 'info':
    default:
      emailProgressStyle.value = 'bg-info';
      break;
  }
};

const signalRestoreResend = () => {
  if (user.value.restorePassword) {
    user.value.restorePassword.emailProgressMessage = null;
    user.value.restorePassword.emailProgressType = null;
  }
  emailProgress.value = 'Prepare to sending ...';
  emailProgressStyle.value = 'bg-info';
};

const signalRestoreConfirm = () => {
  unsetErrorReason();
  disabledSubmit.value = false;
  store.dispatch('auth/restoreConfirm');
  emailProgress.value = 'Prepare to sending ...';
  emailProgressStyle.value = 'bg-info';
  showToast('Почта подтверждена', 'Восстановление завершено успешно', 5000);
  emit('switchMode', 'restoreConfirm');
};

onMounted(() => {
  if (user.value.restorePassword) {
    emailProgress.value = user.value.restorePassword.emailProgressMessage === null ? 'Prepare to sending ...' : user.value.restorePassword.emailProgressMessage;
    switch (user.value.restorePassword.emailProgressType) {
      case 'error':
        emailProgressStyle.value = 'bg-danger';
        break;
      case null:
      case 'info':
      default:
        emailProgressStyle.value = 'bg-info';
        break;
    }
  }
});
</script>

<style scoped>

</style>